import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Divider,
  Chip,
  IconButton,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
  BottomNavigation,
  BottomNavigationAction,
  CircularProgress
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  Mail as MailIcon
} from '@mui/icons-material';
import EmailsTab from './EmailsTab';
import TimelineTab from './TimelineTab';
import { useParams, useLocation } from 'react-router-dom';
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";
import { CanWrite } from '../../context/AuthContext';

const RequestDetailsComponent = ({ requestData: propRequestData, onClose }) => {
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [requestData, setRequestData] = useState(propRequestData || null);
  const [loading, setLoading] = useState(!propRequestData);
  const [error, setError] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    // Only fetch if requestData wasn't passed as prop
    if (!propRequestData) {
      getRequestUserData();
    }
  }, [propRequestData, location.state?.requestId, id]);

  const getRequestUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const requestId = location.state?.requestId || id;
      
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getRequestUserData`,
        {},
        { requestId },
        "GET"
      );
      
      if (isSuccess && data?.data) {
        setRequestData(data.data);
      } else {
        setError('Failed to load request data');
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCopyUrl = async (url, urlType) => {
    const requestId = requestData?.id || location.state?.requestId || id;
    if (!requestId) {
      return;
    }

    // Map urlType to roleType
    const roleTypeMap = {
      'main': 'mainContact',
      'data': 'siteAdmin',
      'photo': 'siteManager'
    };
    const roleType = roleTypeMap[urlType];

    // Check if URL is a placeholder or invalid
    const isPlaceholderUrl = url && (
      url.includes('example.com') || 
      url.includes('localhost') ||
      !url.includes('?token=')
    );

    // If URL doesn't exist or is a placeholder, generate it first
    if ((!url || isPlaceholderUrl) && roleType) {
      try {
        setGeneratingLink(true);
        // Generate and save the specific link
        const { isSuccess, data } = await apiCall(
          `${config.POST_LOGIN_API_URL}getLoginLinks`,
          {},
          { requestId, roleType },
          'GET'
        );

        if (isSuccess && data?.data) {
          const generatedUrl = roleType === 'mainContact' ? data.data.mainContactLoginUrl :
                               roleType === 'siteAdmin' ? data.data.siteAdminLoginUrl :
                               data.data.siteManagerLoginUrl;

          if (generatedUrl && !generatedUrl.includes('example.com')) {
            // Update local state
            setRequestData(prev => ({
              ...prev,
              mainContactLoginUrl: roleType === 'mainContact' ? generatedUrl : prev?.mainContactLoginUrl,
              siteAdminLoginUrl: roleType === 'siteAdmin' ? generatedUrl : prev?.siteAdminLoginUrl,
              siteManagerLoginUrl: roleType === 'siteManager' ? generatedUrl : prev?.siteManagerLoginUrl,
              mainContactUrl: roleType === 'mainContact' ? generatedUrl : prev?.mainContactUrl,
              dataFormUrl: roleType === 'siteAdmin' ? generatedUrl : prev?.dataFormUrl,
              photoFormUrl: roleType === 'siteManager' ? generatedUrl : prev?.photoFormUrl,
            }));

            navigator.clipboard.writeText(generatedUrl);
            setCopiedUrl(urlType);
            setTimeout(() => setCopiedUrl(''), 2000);
          } else {
            console.error('Generated URL is still a placeholder or invalid');
          }
        }
      } catch (error) {
        console.error('Error generating link:', error);
      } finally {
        setGeneratingLink(false);
      }
    } else if (url && !isPlaceholderUrl) {
      navigator.clipboard.writeText(url);
      setCopiedUrl(urlType);
      setTimeout(() => setCopiedUrl(''), 2000);
    } else if (isPlaceholderUrl) {
      // If it's a placeholder, try to generate a new one
      handleCopyUrl(null, urlType);
    }
  };

  const handleSendReminder = async (urlType) => {
    const requestId = requestData?.id || location.state?.requestId || id;
    if (!requestId) {
      return;
    }

    // Map urlType to recipientType
    const recipientTypeMap = {
      'main': 'mainContact',
      'data': 'siteAdmin',
      'photo': 'siteManager'
    };
    const recipientType = recipientTypeMap[urlType];

    if (!recipientType) {
      return;
    }

    try {
      setSendingReminder(true);
      
      // First, ensure the link is generated
      const { isSuccess: linkSuccess, data: linkData } = await apiCall(
        `${config.POST_LOGIN_API_URL}getLoginLinks`,
        {},
        { requestId, roleType: recipientType },
        'GET'
      );

      if (linkSuccess && linkData?.data) {
        // Update local state with generated link
        const generatedUrl = recipientType === 'mainContact' ? linkData.data.mainContactLoginUrl :
                             recipientType === 'siteAdmin' ? linkData.data.siteAdminLoginUrl :
                             linkData.data.siteManagerLoginUrl;

        if (generatedUrl) {
          setRequestData(prev => ({
            ...prev,
            mainContactLoginUrl: recipientType === 'mainContact' ? generatedUrl : prev?.mainContactLoginUrl,
            siteAdminLoginUrl: recipientType === 'siteAdmin' ? generatedUrl : prev?.siteAdminLoginUrl,
            siteManagerLoginUrl: recipientType === 'siteManager' ? generatedUrl : prev?.siteManagerLoginUrl,
            mainContactUrl: recipientType === 'mainContact' ? generatedUrl : prev?.mainContactUrl,
            dataFormUrl: recipientType === 'siteAdmin' ? generatedUrl : prev?.dataFormUrl,
            photoFormUrl: recipientType === 'siteManager' ? generatedUrl : prev?.photoFormUrl,
          }));
        }
      }

      // Send reminder using the reminder endpoint
      const status = requestData?.status || 0;
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}reminder/send`,
        {},
        {
          status,
          requestId,
          recipientType: [recipientType]
        },
        'POST'
      );

      if (isSuccess) {
        // Success message is handled by apiCall
        console.log('Reminder sent successfully');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
    } finally {
      setSendingReminder(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  // Map API response to component structure
  const request = requestData ? {
    id: requestData.id,
    araId: requestData.araId,
    companyName: requestData.companyName,
    siteAddress: requestData.siteAddress,
    siteCity: requestData.siteCity,
    siteState: requestData.siteState,
    siteZipcode: requestData.siteZipcode,
    reference: requestData.zohoId || 'N/A',
    inspectionDueDate: requestData.scheduledDate,
    approvedDate: requestData.approvedDate,
    inspectionDate: requestData.inspectionDate,
    completedDate: requestData.completedDate,
    nextRenewalDate: null, // Add if available in API
    contactName: requestData.contactName,
    contactPhone: requestData.contactPhone,
    contactEmail: requestData.contactEmail,
    contactCheckPassword: requestData.contactCheckPassword,
    inspector: null, // Add if available in API
    siteAdminName: requestData.siteAdminName,
    siteAdminEmail: requestData.siteAdminEmail,
    siteAdminPhone: requestData.siteAdminPhone,
    siteAdminDesignation: requestData.siteAdminDesignation,
    siteAdminCheckPassword: requestData.siteAdminCheckPassword,
    siteManagerName: requestData.siteManagerName,
    siteManagerEmail: requestData.siteManagerEmail,
    siteManagerPhone: requestData.siteManagerPhone,
    siteManagerDesignation: requestData.siteManagerDesignation,
    siteManagerCheckPassword: requestData.siteManagerCheckPassword,
    mainContactUrl: requestData.mainContactLoginUrl || requestData.mainContactUrl,
    photoFormUrl: requestData.siteManagerLoginUrl || requestData.photoFormUrl,
    dataFormUrl: requestData.siteAdminLoginUrl || requestData.dataFormUrl,
    mainContactLoginUrl: requestData.mainContactLoginUrl,
    siteAdminLoginUrl: requestData.siteAdminLoginUrl,
    siteManagerLoginUrl: requestData.siteManagerLoginUrl,
    dataFormCompleted: requestData.isDataFormComplete === 1,
    pictureFormCompleted: requestData.isPictureFormComplete === 1,
    emailCount: requestData.emailCount || 0,
    status: requestData.status,
    statusBeforeExpiry: requestData.statusBeforeExpiry,
    companyId: requestData.companyId,
    userId: requestData.userId,
    siteId: requestData.siteId
  } : null;

  const InfoRow = ({ label, value, icon: Icon }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Icon && <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || 'N/A'}
        </Typography>
      </Box>
    </Box>
  );

  const ContactCard = ({ title, name, email, phone }) => (
    <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
        {title}
      </Typography>
      <InfoRow label="Name" value={name} icon={PersonIcon} />
      <InfoRow label="Email" value={email} icon={EmailIcon} />
      <InfoRow label="Phone" value={phone} icon={PhoneIcon} />
    </Paper>
  );

  const UrlRow = ({ label, url, urlType }) => {
    // Get the correct URL based on urlType
    const getUrl = () => {
      if (url) return url;
      // Fallback to login URLs from requestData
      if (urlType === 'main') return requestData?.mainContactLoginUrl || requestData?.mainContactUrl;
      if (urlType === 'data') return requestData?.siteAdminLoginUrl || requestData?.dataFormUrl;
      if (urlType === 'photo') return requestData?.siteManagerLoginUrl || requestData?.photoFormUrl;
      return null;
    };

    const currentUrl = getUrl();

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        mb: 2,
        pb: 2,
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{marginRight:2, flex: 1, width: '100%' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
          <Tooltip title={copiedUrl === urlType ? "Copied!" : currentUrl ? "Copy URL" : "Generate and Copy URL"}>
            <IconButton 
              size="small" 
              onClick={() => handleCopyUrl(currentUrl, urlType)}
              disabled={generatingLink}
              sx={{ color: '#0050a4',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              {generatingLink ? <CircularProgress size={16} /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {/* <CanWrite> */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleSendReminder(urlType)}
            disabled={sendingReminder}
            sx={{ 
              color: '#F5A622',
              borderColor: '#F5A622',
              textTransform: 'none',
              fontSize: '0.75rem',
              minWidth: 'auto',
              px: 2,
              flex: isMobile ? 1 : 'none'
            }}
          >
            {sendingReminder ? 'Sending...' : 'Send Reminder'}
          </Button>
            {/* </CanWrite> */}
        </Box>
      </Box>
    );
  };

  const StatusIndicator = ({ completed, label }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      p: 2,
      borderRadius: 1,
    }}>
      <CheckCircleIcon sx={{ color: completed ? '#4caf50' : '#ff9800', fontSize: 20 }} />
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
        {label}
      </Typography>
    </Box>
  );

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        gap: 2
      }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" onClick={getRequestUserData}>
          Retry
        </Button>
      </Box>
    );
  }

  // No data state
  if (!request) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Typography variant="h6">No request data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      bgcolor: 'background.paper',
      pt: 2,
      px: 3,
      pb: isMobile ? 8 : 0
    }}>
      {/* Basic Info Grid */}
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: '#F1F7FF' }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={6} sm={6} md={3}>
            <InfoRow label="MSI ID" value={request.id} />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <InfoRow label="ARA ID" value={request.araId} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoRow label="Company Name" value={request.companyName} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoRow 
              label="Address" 
              value={`${request.siteAddress}, ${request.siteCity}, ${request.siteState}${request.siteZipcode ? `, ${request.siteZipcode}` : ''}`} 
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Desktop Tabs */}
      {!isMobile && (
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              bgcolor: '#0050A4',
              '& .MuiTab-root': { 
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 500
              },
              '& .Mui-selected': { 
                color: 'white !important'
              },
              '& .MuiTabs-indicator': {
                bgcolor: 'white'
              }
            }}
          >
            <Tab sx={{marginLeft:isMobile ? 0 : 3, marginRight: isMobile ? 0 : 3 }} label="Details" />
            <Tab sx={{ marginRight: isMobile ? 0 : 3 }} label={`Emails ${request.emailCount ? `(${request.emailCount})` : ''}`} />
            <Tab sx={{ marginRight: isMobile ? 0 : 3 }} label="Timeline" />
          </Tabs>
        </Paper>
      )}

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3, fontWeight: 600 , p:1}}>
            Additional Details
          </Typography>
          
          <Paper sx={{ p: isMobile ? 2 : 3, mb: 2, bgcolor:'#F1F7FF' }}>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow label="Reference" value={request.reference} />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow 
                  label="Inspection Due Date" 
                  value={formatDate(request.inspectionDueDate)} 
                  icon={CalendarIcon}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow 
                  label="Approved Date" 
                  value={formatDate(request.approvedDate)} 
                  icon={CalendarIcon}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow 
                  label="Inspection Date" 
                  value={formatDate(request.inspectionDate)} 
                  icon={CalendarIcon}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow label="Contact Name" value={request.contactName} icon={PersonIcon} />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <InfoRow label="Contact Phone" value={request.contactPhone} icon={PhoneIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoRow label="Contact Email" value={request.contactEmail} icon={EmailIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoRow label="Inspector" value={request.inspector} icon={PersonIcon} />
              </Grid>
            </Grid>
          </Paper>

          {/* Site Admin Contact */}
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3, fontWeight: 600 , p:1}}>
              Site Admin Contact
            </Typography>
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 2, bgcolor:'#F1F7FF' }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Name" value={request.siteAdminName} icon={PersonIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Email" value={request.siteAdminEmail} icon={EmailIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Phone" value={request.siteAdminPhone} icon={PhoneIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Designation" value={request.siteAdminDesignation} />
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Site Manager Contact */}
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3, fontWeight: 600 , p:1}}>
              Site Manager Contact
            </Typography>
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 2, bgcolor:'#F1F7FF' }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Name" value={request.siteManagerName} icon={PersonIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Email" value={request.siteManagerEmail} icon={EmailIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Contact Phone" value={request.siteManagerPhone} icon={PhoneIcon} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InfoRow label="Designation" value={request.siteManagerDesignation} />
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Quick Links */}
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3, fontWeight: 600 , p:1}}>
              Quick Links
            </Typography>
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 2 ,bgcolor:'#F1F7FF',display:'flex',flexDirection:'row',flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
              <UrlRow 
                label="Main Contact URL" 
                url={request.mainContactLoginUrl || request.mainContactUrl}
                urlType="main"
              />
              <UrlRow 
                label="Data Form URL" 
                url={request.siteAdminLoginUrl || request.dataFormUrl}
                urlType="data"
              />
              <UrlRow 
                label="Photo Form URL" 
                url={request.siteManagerLoginUrl || request.photoFormUrl}
                urlType="photo"
              />
            </Paper>
          </Box>

          {/* Status */}
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ mb: 3, fontWeight: 600 }}>
              Status
            </Typography>
            <Paper sx={{ p: isMobile ? 2 : 3, marginBottom:3,bgcolor:'#F1F7FF'}}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StatusIndicator 
                    completed={request.dataFormCompleted}
                    label="Data Form Completed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusIndicator 
                    completed={request.pictureFormCompleted}
                    label="Picture Form Completed"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Box>
      )}

      {/* Emails Tab */}
      {activeTab === 1 && (
        <EmailsTab requestId={request.id} />
      )}

      {/* Timeline Tab */}
      {activeTab === 2 && (
        <TimelineTab requestId={request.id} />
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderTop: '1px solid #e0e0e0'
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={activeTab}
            onChange={handleTabChange}
            showLabels
            sx={{
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 12px'
              },
              '& .Mui-selected': {
                color: '#1976d2'
              }
            }}
          >
            <BottomNavigationAction 
              label="Details" 
              icon={<DescriptionIcon />} 
            />
            <BottomNavigationAction 
              label={`Emails ${request.emailCount ? `(${request.emailCount})` : ''}`}
              icon={<MailIcon />} 
            />
            <BottomNavigationAction 
              label="Timeline" 
              icon={<TimelineIcon />} 
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default RequestDetailsComponent;